import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import Image from 'components/Image';

import { sendEvent } from 'lib/gtag';
import { getOfferImageLink, toOfferImageAltText } from 'lib/image';

const Hero = ({
  className,
  gaEventCategory,
  isFinished,
  offerId,
  offerPhoto,
  offerTitle,
  overlayActions = null,
}) => {
  const [isOfferImgLoaded, setOfferImgLoaded] = useState(true);
  const router = useRouter();

  const onOfferImageClick = () => {
    sendEvent({ action: 'image', category: gaEventCategory });
  };

  /**
   * As this image can take a bit to load depending on the users network speed,
   * we make sure to remove the last offer image showed. As we don't use Next
   * official `Image` component, we got to do a little bit of work to achieve
   * similar results and keep a good UX.
   *
   * TODO: might be a good idea to add a skeleton or a small, blurred version
   * of the image before loading the actual offer image.
   */
  useEffect(() => {
    const onOfferImageLoad = () => {
      if (!isOfferImgLoaded) {
        setOfferImgLoaded(true);
      }
    };

    const validateImageLoading = () => {
      const offerImageElem = document?.querySelector(`#offer-img-${offerId}`);

      if (!offerImageElem) {
        return;
      }

      if (offerImageElem.complete) {
        setOfferImgLoaded(true);
        return;
      }

      offerImageElem.addEventListener('load', onOfferImageLoad);
    };

    router.events.on('routeChangeComplete', validateImageLoading);

    return () => {
      document
        ?.querySelector(`#offer-img-${offerId}`)
        ?.removeEventListener('load', onOfferImageLoad);
      router.events.off('routeChangeComplete', validateImageLoading);
    };
  }, [isOfferImgLoaded, offerId, setOfferImgLoaded]);

  useEffect(() => {
    const setOfferImgLoadedAsFalse = () => {
      setOfferImgLoaded(false);
    };

    router.events.on('routeChangeStart', setOfferImgLoadedAsFalse);

    return () => {
      router.events.off('routeChangeStart', setOfferImgLoadedAsFalse);
    };
  }, [offerId, setOfferImgLoaded]);

  return (
    <div
      className={twMerge(
        'relative flex w-full items-center justify-center rounded-5 border border-rd-neutral-300 py-4 dark:border-neutral-low-400 lg:h-[304px] xl:min-w-[340px]',
        className
      )}
    >
      <Image
        alt={toOfferImageAltText(offerTitle)}
        className={twJoin(
          'mx-auto block rounded-2 lg:size-[260px]',
          isOfferImgLoaded ? 'opacity-100' : 'opacity-0'
        )}
        grayscale={isFinished}
        height={168}
        id={`offer-img-${offerId}`}
        loading={null}
        sizes="(max-width: 1024px) 168px, 268px"
        src={getOfferImageLink(offerPhoto, 168)}
        srcSet={`
          ${getOfferImageLink(offerPhoto, 168)} 168w,
          ${getOfferImageLink(offerPhoto, 200)} 200w,
          ${getOfferImageLink(offerPhoto, 300)} 300w
          ${getOfferImageLink(offerPhoto, 400)} 400w
        `}
        width={168}
        priority
        onClick={onOfferImageClick}
      />
      {overlayActions && (
        <div className="absolute right-3 top-3 flex flex-col items-end gap-2">
          {overlayActions}
        </div>
      )}
    </div>
  );
};

Hero.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  isFinished: PropTypes.bool.isRequired,
  offerId: PropTypes.number.isRequired,
  offerPhoto: PropTypes.string.isRequired,
  offerTitle: PropTypes.string.isRequired,
  /**
   * Ações renderizadas sobre a imagem, no canto superior direito. Usado
   * para botões secundários como "reportar" e "compartilhar" que não
   * competem com o CTA principal (Ir à loja).
   */
  overlayActions: PropTypes.node,
};

export default Hero;
